import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  Archive,
  ArrowRight,
  Printer,
  RefreshCw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import useSystemSettings from '../hooks/useSystemSettings';
import {
  fetchProfileLookups,
  formatProdi,
  findProfileProdi,
  hydrateProfile,
} from '../lib/profileLookups';
import { formatRoleLabel, getCriteriaScope } from '../lib/accessRules';
import { fetchKriteriaList, mapCriteriaForAccess } from '../lib/kriteriaMetadata';

const getStatus = (progress) => {
  if (progress === 0) return 'Belum Ada';
  if (progress < 100) return 'Proses';
  return 'Lengkap';
};

function LaporanPage() {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();

  const [profile, setProfile] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [matchedProdi, setMatchedProdi] = useState(null);
  const [prodiList, setProdiList] = useState([]);
  const [selectedProdiId, setSelectedProdiId] = useState('');
  const [globalStats, setGlobalStats] = useState({
    activeUsers: 0,
    totalProdi: 0,
  });
  const [lastAccess, setLastAccess] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');

  const isSuperadmin = profile?.role === 'superadmin';

  const formatDateTime = (value) => {
    if (!value) return '-';

    return new Date(value).toLocaleString('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const statusVariant = (status) => {
    if (status === 'Lengkap') return 'success';
    if (status === 'Proses') return 'warning';
    return 'secondary';
  };

  const countCriteriaRows = async ({ criteria, prodiId }) => {
    let query = supabase
      .schema('akreditasi')
      .from('kriteria_data')
      .select('*', { count: 'exact' })
      .eq('kriteria_id', criteria.criteriaId)
      .eq('is_deleted', false);

    if (prodiId) {
      query = query.eq('prodi_id', prodiId);
    }

    const { data, count, error } = await query;

    if (error) {
      throw new Error(`Gagal mengambil data ${criteria.code}: ${error.message}`);
    }

    const total = count || 0;
    const fields = criteria.fields || [];
    const filledColumns = (data || []).reduce(
      (sum, item) =>
        sum +
        fields.filter((field) => {
          const value = criteria.dynamic ? item.data_json?.[field] : item[field];
          return value !== null && value !== undefined && String(value).trim() !== '';
        }).length,
      0
    );
    const totalColumns = (data || []).length * fields.length;
    const progress = totalColumns > 0 ? Math.round((filledColumns / totalColumns) * 100) : 0;
    const latestDate = (data || [])
      .map((item) => item.created_at)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];

    return {
      ...criteria,
      total,
      progress,
      filledColumns,
      totalColumns,
      status: getStatus(progress),
      latestDate,
    };
  };

  const loadGlobalStats = async () => {
    const [usersResult, prodiResult] = await Promise.all([
      supabase
        .schema('akreditasi')
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true),
      supabase
        .schema('akreditasi')
        .from('prodi')
        .select('id', { count: 'exact', head: true }),
    ]);

    if (usersResult.error) {
      throw new Error(`Gagal menghitung user aktif: ${usersResult.error.message}`);
    }

    if (prodiResult.error) {
      throw new Error(`Gagal menghitung prodi: ${prodiResult.error.message}`);
    }

    return {
      activeUsers: usersResult.count || 0,
      totalProdi: prodiResult.count || 0,
    };
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    setInfoMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    const { data: profileData, error: profileError } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profileError || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!profileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    try {
      const lookups = await fetchProfileLookups(supabase);
      const hydratedProfile = hydrateProfile(profileData, lookups);
      setProfile(hydratedProfile);
      setProdiList(lookups.prodiList || []);

      const isGlobalMode = profileData.role === 'superadmin';
      let prodiFilterId = null;
      let profileProdiData = null;

      if (isGlobalMode) {
        const nextGlobalStats = await loadGlobalStats();
        setGlobalStats(nextGlobalStats);
        profileProdiData =
          lookups.prodiList.find((item) => item.id === selectedProdiId) ||
          lookups.prodiList[0] ||
          null;
        setMatchedProdi(profileProdiData);
        prodiFilterId = profileProdiData?.id || null;
        if (!selectedProdiId && profileProdiData?.id) {
          setSelectedProdiId(profileProdiData.id);
        }
      } else {
        profileProdiData = findProfileProdi(profileData, lookups.prodiList);
        setMatchedProdi(profileProdiData);

        if (profileProdiData) {
          prodiFilterId = profileProdiData.id;
        } else {
          setInfoMsg(
            'Kode prodi pada profile belum cocok dengan tabel akreditasi.prodi, sehingga laporan prodi belum dapat difilter berdasarkan prodi_id.'
          );
        }
      }

      const dynamicCriteria = await fetchKriteriaList(supabase, {
        prodiId: prodiFilterId,
      });
      const criteriaReports = mapCriteriaForAccess(dynamicCriteria).map((criteria) => ({
        code: criteria.kode_kriteria,
        name: criteria.nama_kriteria,
        route: isGlobalMode && prodiFilterId
          ? `${criteria.route}?prodi=${prodiFilterId}`
          : criteria.route,
        criteriaId: criteria.id,
        dynamic: true,
        fields: (criteria.fields || []).map((field) => field.nama_field),
      }));

      const criteriaScope = getCriteriaScope(profileData, criteriaReports);
      const results = await Promise.all(
        criteriaScope.map((criteria) =>
          countCriteriaRows({
            criteria,
            prodiId: prodiFilterId,
          })
        )
      );

      setReportRows(results);
      setLastAccess(new Date());
    } catch (error) {
      setErrorMsg(error.message);
    }

    setLoading(false);
  }, [navigate, selectedProdiId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const summary = useMemo(() => {
    const totalData = reportRows.reduce((sum, item) => sum + item.total, 0);
    const totalFilledColumns = reportRows.reduce(
      (sum, item) => sum + (item.filledColumns || 0),
      0
    );
    const totalColumns = reportRows.reduce(
      (sum, item) => sum + (item.totalColumns || 0),
      0
    );
    const filledCriteria = reportRows.filter((item) => item.total > 0).length;
    const incompleteCriteria = reportRows.filter((item) => item.status !== 'Lengkap').length;
    const overallProgress =
      totalColumns > 0 ? Math.round((totalFilledColumns / totalColumns) * 100) : 0;

    return {
      totalData,
      totalFilledColumns,
      totalColumns,
      filledCriteria,
      incompleteCriteria,
      overallProgress,
    };
  }, [reportRows]);

  const attentionRows = useMemo(
    () => reportRows.filter((item) => item.progress < 80),
    [reportRows]
  );

  const reportTitle = 'Laporan Akreditasi Program Studi';

  const reportScope = matchedProdi
      ? `${matchedProdi.jenjang} - ${matchedProdi.nama_prodi}`
      : profile?.prodi_display || profile?.prodi || '-';

  const handleSelectedProdiChange = (value) => {
    setSelectedProdiId(value);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Laporan Akreditasi"
        breadcrumbs={['Dashboard', 'Laporan']}
        profile={profile}
        activeMenu="laporan"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Laporan Akreditasi"
      breadcrumbs={['Dashboard', 'Laporan']}
      profile={profile}
      activeMenu="laporan"
    >
      <Container fluid className="p-0">
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
        {infoMsg && <Alert variant="warning">{infoMsg}</Alert>}

        <Card className="report-hero-card mb-4">
          <Card.Body>
            <div className="report-hero-main">
              <div className="hero-eyebrow-v2">
                {isSuperadmin ? 'Rekap Superadmin per Prodi' : 'Rekap Prodi'}
              </div>
              <h2 className="report-hero-title">{reportTitle}</h2>
              <div className="report-meta-grid">
                <div>
                  <span>Program Studi</span>
                  <strong>{reportScope}</strong>
                </div>
                <div>
                  <span>{isSuperadmin ? 'Kampus' : 'Role'}</span>
                  <strong>
                    {isSuperadmin
                      ? settings.nama_kampus
                      : formatRoleLabel(profile)}
                  </strong>
                </div>
                <div>
                  <span>Periode Akreditasi</span>
                  <strong>{settings.periode_akreditasi}</strong>
                </div>
                <div>
                  <span>Tanggal Akses</span>
                  <strong>{formatDateTime(lastAccess)}</strong>
                </div>
              </div>
            </div>

            <div className="report-hero-actions">
              {isSuperadmin && (
                <Form.Select
                  className="criterion-select-compact"
                  value={selectedProdiId}
                  onChange={(e) => handleSelectedProdiChange(e.target.value)}
                >
                  {prodiList.map((prodi) => (
                    <option value={prodi.id} key={prodi.id}>
                      {formatProdi(prodi)}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Button className="archive-btn archive-btn-blue" onClick={handlePrint}>
                <Printer size={16} className="me-2" />
                Cetak
              </Button>
              <Button variant="light" className="archive-btn-back" onClick={() => navigate('/arsip')}>
                <Archive size={16} className="me-2" />
                Arsip Data
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Row className="g-4 mb-4">
          <Col xl={3} md={6}>
            <Card className="content-card-v2 h-100">
              <Card.Body>
                <div className="section-subtitle-v2">
                  {isSuperadmin ? 'Total Data Akreditasi' : 'Total Data Akreditasi Prodi'}
                </div>
                <div className="summary-number-v2">{summary.totalData}</div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="content-card-v2 h-100">
              <Card.Body>
                <div className="section-subtitle-v2">
                  {isSuperadmin ? 'Kriteria Terisi Prodi' : 'Kriteria Terisi'}
                </div>
                <div className="summary-number-v2">
                  {summary.filledCriteria}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="content-card-v2 h-100">
              <Card.Body>
                <div className="section-subtitle-v2">
                  {isSuperadmin ? 'Total Prodi Sistem' : 'Belum Lengkap'}
                </div>
                <div className="summary-number-v2">
                  {isSuperadmin ? globalStats.totalProdi : summary.incompleteCriteria}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="content-card-v2 h-100">
              <Card.Body>
                <div className="section-subtitle-v2">
                  {isSuperadmin ? 'Progress Prodi' : 'Progress Keseluruhan'}
                </div>
                <div className="summary-number-v2">
                  {summary.overallProgress}%
                </div>
                <ProgressBar now={summary.overallProgress} className="mt-3" />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4">
          <Col xl={8}>
            <Card className="content-card-v2 report-card">
              <Card.Body>
                <div className="section-head-v2 mb-3">
                  <div>
                    <h4 className="section-title-v2">Rekap Per Kriteria</h4>
                  </div>
                  <Button variant="outline-primary" className="toolbar-btn" onClick={loadReport}>
                    <RefreshCw size={16} className="me-2" />
                    Muat Ulang
                  </Button>
                </div>

                <Table responsive hover className="criterion-table align-middle">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Kriteria</th>
                      <th>Nama Kriteria</th>
                      <th>Jumlah Baris</th>
                      <th>Kolom Terisi</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.map((item, index) => (
                      <tr key={item.code}>
                        <td>{index + 1}</td>
                        <td>
                          <Badge bg="primary">{item.code}</Badge>
                        </td>
                        <td>{item.name}</td>
                        <td>{item.total}</td>
                        <td>
                          {item.filledColumns}/{item.totalColumns}
                        </td>
                        <td>
                          <Badge bg={statusVariant(item.status)}>{item.status}</Badge>
                        </td>
                        <td>
                          <div className="report-progress-cell">
                            <ProgressBar now={item.progress} />
                            <span>{item.progress}%</span>
                          </div>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => navigate(item.route)}
                          >
                            Lihat
                            <ArrowRight size={14} className="ms-1" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="content-card-v2 report-card">
              <Card.Body>
                <h4 className="section-title-v2 mb-3">
                  {isSuperadmin
                    ? 'Kriteria yang Perlu Perhatian'
                    : 'Kriteria yang Perlu Dilengkapi'}
                </h4>
                {attentionRows.length > 0 ? (
                  <div className="report-attention-list">
                    {attentionRows.map((item) => (
                      <button
                        type="button"
                        className="report-attention-item"
                        key={item.code}
                        onClick={() => navigate(item.route)}
                      >
                        <div>
                          <strong>{item.code}</strong>
                          <span>{item.name}</span>
                        </div>
                        <Badge bg={statusVariant(item.status)}>{item.progress}%</Badge>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="criterion-softdelete-note">
                    Semua kriteria sudah berada pada progress tinggi.
                  </div>
                )}
              </Card.Body>
            </Card>

            {isSuperadmin && (
              <Card className="content-card-v2 report-card mt-4">
                <Card.Body>
                  <h4 className="section-title-v2 mb-3">Distribusi Global</h4>
                  <div className="archive-side-meta">
                    <div>
                      <strong>Data Aktif</strong> : {summary.totalData}
                    </div>
                    <div>
                      <strong>User Aktif</strong> : {globalStats.activeUsers}
                    </div>
                    <div>
                      <strong>Program Studi</strong> : {globalStats.totalProdi}
                    </div>
                    <div>
                      <strong>Kriteria Terisi</strong> : {summary.filledCriteria}/{reportRows.length}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </DashboardLayout>
  );
}

export default LaporanPage;
